/**
 * 文件作用：AI 厂商 (Provider) 配置层
 * - 定义 ProviderConfig 接口，承载厂商元信息（名称、Logo、官网、API 配置等）
 * - 提供查找函数 getProviderConfig / listProviderConfigs
 * - 与 models.config.ts 配合，实现 Provider-Model 分层架构
 */

export type ProviderId =
  | 'openai'
  | 'google'
  | 'alibaba'
  | 'bytedance'
  | 'replicate'
  | 'kling'
  | 'xai'
  | 'fal'
  | 'example'
  | 'local';

export interface ProviderApiDefaults {
  baseURL: string;
  authType: 'bearer' | 'apiKey' | 'custom';
  headers?: Record<string, string>;
  query?: Record<string, string>;
}

export interface ProviderConfig {
  id: ProviderId;
  displayName: string;
  logo?: string;
  website?: string;
  docUrl?: string;
  description?: string;
  apiDefaults?: ProviderApiDefaults;
  aliases?: string[];
  metadata?: Record<string, unknown>;
}

const providers: ProviderConfig[] = [
  {
    id: 'openai',
    displayName: 'OpenAI',
    logo: '/providers/openai.svg',
    website: 'https://openai.com',
    docUrl: 'https://platform.openai.com/docs',
    description: '提供 Sora 等文本、图像与视频模型的 AI 厂商。',
    apiDefaults: {
      baseURL: 'https://api.openai.com',
      authType: 'bearer',
    },
    aliases: ['openai', 'sora'],
  },
  {
    id: 'google',
    displayName: 'Google AI',
    logo: '/providers/google.svg',
    website: 'https://ai.google.dev',
    docUrl: 'https://ai.google.dev/gemini-api',
    description: '提供 Veo、Gemini 等多模态模型的 AI 厂商。',
    apiDefaults: {
      baseURL: 'https://generativelanguage.googleapis.com',
      authType: 'bearer',
    },
    aliases: ['google', 'google-ai', 'gemini', 'veo'],
  },
  {
    id: 'alibaba',
    displayName: '阿里百炼',
    website: 'https://bailian.console.aliyun.com',
    docUrl: 'https://help.aliyun.com/zh/bailian/',
    description: '阿里百炼 AIGC 服务（视频生成等）。',
    apiDefaults: {
      baseURL: 'https://www.wyjh.top',
      authType: 'bearer',
    },
    aliases: ['alibaba', 'alibailian', 'bailian', 'dashscope'],
  },
  {
    id: 'bytedance',
    displayName: '字节跳动（豆包）',
    logo: '/providers/bytedance.svg',
    website: 'https://www.doubao.com',
    docUrl: 'https://www.volcengine.com/docs/82379',
    description: '豆包 Seedream / Seedance 图像与视频模型。',
    apiDefaults: {
      baseURL: 'https://open.volcengineapi.com',
      authType: 'custom',
    },
    aliases: ['bytedance', 'volcengine', 'doubao'],
  },
  {
    id: 'replicate',
    displayName: 'Replicate',
    logo: '/providers/replicate.svg',
    website: 'https://replicate.com',
    docUrl: 'https://replicate.com/docs',
    description: '第三方模型聚合平台，支持 Bytedance Seedream 等模型聚合调用。',
    apiDefaults: {
      baseURL: 'https://api.replicate.com',
      authType: 'bearer',
    },
    aliases: ['replicate'],
  },
  {
    id: 'kling',
    displayName: 'Kling AI（可灵）',
    logo: '/providers/kling.svg',
    website: 'https://klingai.com',
    docUrl: 'https://klingai.com/docs',
    description: '可灵视频生成与自动对口型模型。',
    apiDefaults: {
      baseURL: 'https://api.klingai.com',
      authType: 'bearer',
    },
    aliases: ['kling'],
  },
  {
    id: 'xai',
    displayName: 'xAI',
    logo: '/providers/xai.svg',
    website: 'https://x.ai',
    docUrl: 'https://docs.x.ai',
    description: '提供 Grok 系列视频生成模型的 AI 厂商。',
    apiDefaults: {
      baseURL: 'https://api.x.ai',
      authType: 'bearer',
    },
    aliases: ['xai', 'grok'],
  },
  {
    id: 'fal',
    displayName: 'Fal.ai',
    logo: '/providers/fal.svg',
    website: 'https://fal.ai',
    docUrl: 'https://fal.ai/docs',
    description: '托管 Nano-banana 等图像模型的推理平台。',
    apiDefaults: {
      baseURL: 'https://fal.run',
      authType: 'bearer',
    },
    aliases: ['fal', 'fal-ai', 'nanobanana', 'nano-banana'],
  },
  {
    id: 'example',
    displayName: 'Example Provider',
    logo: '/providers/example.svg',
    description: '演示/测试用虚拟 Provider，不绑定真实 API。',
    apiDefaults: undefined,
    aliases: ['example'],
  },
  {
    id: 'local',
    displayName: 'Local Processing',
    description: '浏览器内图像/视频处理工具',
    logo: '/providers/local.svg',
    aliases: ['local', 'builtin'],
    apiDefaults: undefined,
  },
];

// 构建索引: id + aliases
const byId = new Map<string, ProviderConfig>();
const byAlias = new Map<string, ProviderConfig>();

providers.forEach((p) => {
  const idKey = p.id.toLowerCase();
  if (!byId.has(idKey)) {
    byId.set(idKey, p);
  }
  (p.aliases ?? []).forEach((alias) => {
    const aliasKey = alias.toLowerCase();
    if (!byAlias.has(aliasKey)) {
      byAlias.set(aliasKey, p);
    }
  });
});

/**
 * 列出所有 Provider 配置
 */
export const listProviderConfigs = (): ProviderConfig[] => [...providers];

/**
 * 根据 id 或 alias 查找 Provider 配置
 * @param identifier - Provider ID 或别名（如 'openai' / 'sora' / 'google' / 'veo'）
 */
export const getProviderConfig = (identifier?: string | null): ProviderConfig | undefined => {
  if (!identifier) return undefined;
  const key = identifier.trim().toLowerCase();
  if (!key) return undefined;
  return byId.get(key) ?? byAlias.get(key);
};

/**
 * 按 Provider ID 分组模型（工具函数）
 * 注意：需要配合 models.config.ts 中的 ModelConfigEntry 使用
 */
export const groupModelsByProvider = <T extends { providerId: ProviderId }>(
  models: T[]
): Map<ProviderId, T[]> => {
  const groups = new Map<ProviderId, T[]>();
  models.forEach((model) => {
    const list = groups.get(model.providerId) ?? [];
    list.push(model);
    groups.set(model.providerId, list);
  });
  return groups;
};
